import {
  Draft,
  DraftConfiguration,
  ProductTask,
  SharingDraft,
  SharingDraftBendingTextConfiguration,
  SharingDraftConfigurationBase,
  SharingDraftDesignConfiguration,
  SharingDraftPlaceholderTextConfiguration,
  SharingDraftSpecialTextConfiguration,
  SharingDraftTextConfiguration,
  Size,
} from '@/types';
import { assertNever } from '@/utils/assertNever';
import { textAlignToAnchor } from '@/utils/text/textAlignToAnchor';
import { getCurrentSizeOfConfiguration } from '@/model/configuration/getCurrentSizeOfConfiguration';
import {
  isDraftBendingTextConfiguration,
  isDraftConfigurationWithText,
  isDraftDesignConfiguration,
  isDraftPlaceholderTextConfiguration,
  isDraftSpecialTextConfiguration,
  isDraftTextConfiguration,
  isDraftTextOrPlaceHolderTextConfiguration,
} from '@/model/configuration/isDraftConfigurationType';
import { isDigital } from '@/model/printType/isDigital';

const getSharingDraftConfigurationBase = (
  {
    offset,
    printArea: { id: printAreaId },
    printType: { id: printTypeId },
    rotation,
  }: DraftConfiguration,
  size: Size
): SharingDraftConfigurationBase => ({
  width: size.width,
  height: size.height,
  offset: { ...offset },
  transform: rotation !== 0 ? `rotate(${rotation}, ${size.width / 2}, ${size.height / 2})` : '',
  printAreaId,
  printTypeId,
});

const getPrintColors = ({ printType, printColors }: DraftConfiguration) => {
  if (printColors.length === 0) {
    return {};
  }

  if (isDigital(printType)) {
    return { printColorRGBs: printColors.map((pc) => pc.fill).join(' ') };
  }

  return {
    printColorId: printColors
      .map((pc) => pc.id)
      .filter(Boolean)
      .join(' '),
  };
};

function getConfigurationDataBasedOnType(configuration: DraftConfiguration, task?: ProductTask) {
  const size = getCurrentSizeOfConfiguration(configuration);
  const sharingDraftConfigurationBase = getSharingDraftConfigurationBase(configuration, size);

  if (isDraftDesignConfiguration(configuration)) {
    const sharingDraftDesignConfiguration: SharingDraftDesignConfiguration = {
      type: 'design',
      ...sharingDraftConfigurationBase,
      designId: configuration.design.id,
      ...getPrintColors(configuration),
    };

    if (configuration.filter) {
      sharingDraftDesignConfiguration.filter = {
        ...configuration.filter,
      };
    }

    if (configuration.mask) {
      // mask
      sharingDraftDesignConfiguration.mask = {
        id: configuration.mask.id,
        offset: configuration.mask.offset,
        scale: configuration.mask.scale,
        version: configuration.mask.version,
      };
    }

    if (task) {
      sharingDraftDesignConfiguration.previewDesignId = task.designId;

      if (sharingDraftDesignConfiguration.filter) {
        sharingDraftDesignConfiguration.filter.designId = task.filterDesignId;
      }
    }

    return sharingDraftDesignConfiguration;
  }

  if (isDraftConfigurationWithText(configuration)) {
    const text = configuration.text;
    const style = text.style;
    const sharingDraftConfigurationWithTextBase = {
      text: text.content,
      ...getPrintColors(configuration),
      fontFamilyId: style.font.familyId,
      fontId: style.font.id,
      fontSize: style.fontSize,
    };

    if (isDraftTextOrPlaceHolderTextConfiguration(configuration)) {
      const sharingDraftTextConfigurationBase = {
        textAnchor: textAlignToAnchor(configuration.text.style.textAlign),
        x: style.x,
        y: style.y,
      };

      if (isDraftTextConfiguration(configuration)) {
        const sharingDraftTextConfiguration: SharingDraftTextConfiguration = {
          type: 'text',
          ...sharingDraftConfigurationBase,
          ...sharingDraftConfigurationWithTextBase,
          ...sharingDraftTextConfigurationBase,
        };

        return sharingDraftTextConfiguration;
      }

      if (isDraftPlaceholderTextConfiguration(configuration)) {
        const sharingDraftPlaceholderTextConfiguration: SharingDraftPlaceholderTextConfiguration = {
          type: 'placeholderText',
          ...sharingDraftConfigurationBase,
          ...sharingDraftConfigurationWithTextBase,
          ...sharingDraftTextConfigurationBase,
          placeholderSize: configuration.placeholderSize,
        };

        return sharingDraftPlaceholderTextConfiguration;
      }
    }

    if (isDraftBendingTextConfiguration(configuration)) {
      const sharingDraftBendingTextConfiguration: SharingDraftBendingTextConfiguration = {
        type: 'bendingText',
        ...sharingDraftConfigurationBase,
        ...sharingDraftConfigurationWithTextBase,
        angle: style.angle,
      };

      if (task && task.size) {
        sharingDraftBendingTextConfiguration.previewDesignId = task.designId;
      }

      return sharingDraftBendingTextConfiguration;
    }
  }

  if (isDraftSpecialTextConfiguration(configuration)) {
    const sharingDraftSpecialTextConfiguration: SharingDraftSpecialTextConfiguration = {
      type: 'specialText',
      ...sharingDraftConfigurationBase,
      text: configuration.text.content,
      fontId: configuration.text.style.font.id,
      textAlign: configuration.text.style.textAlign,
    };

    if (task && task.size) {
      sharingDraftSpecialTextConfiguration.previewDesignId = task.designId;
    }

    return sharingDraftSpecialTextConfiguration;
  }

  assertNever(configuration);
}

export function mapDraftToSharingDraft(draft: Draft, tasks: ProductTask[] = []): SharingDraft {
  const { productType, appearance, view, configurationIds, configurations } = draft;

  return {
    productTypeId: productType.id,
    appearanceId: appearance.id,
    viewId: view.id,
    configurations: configurationIds.map((cid) =>
      getConfigurationDataBasedOnType(
        configurations[cid],
        tasks.find((t) => t.cid === cid)
      )
    ),
  };
}
